import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";

import { planCatalogApi, type PlanCatalogRow } from "../../shared/api/platformBilling";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/** Khuôn mã gói — cùng khuôn máy chủ và ràng buộc trong cơ sở dữ liệu áp. */
const PLAN_CODE = /^[A-Z][A-Z0-9_]{1,31}$/;

const digitsOnly = (value: string) => value.replace(/\D/g, "");

/** Một ô nhập: nhãn ở trên, ghi chú ở dưới. Ghi chú nằm ngoài nhãn để không lẫn vào tên của ô. */
function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <label className="grid gap-1.5">
        <span className="text-sm font-medium">{label}</span>
        {children}
      </label>
      {hint && <div className="mt-1.5 space-y-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function PlanCreateDialog({
  onCreated,
  onClose,
}: {
  onCreated: () => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("0");

  const create = useMutation({
    mutationFn: () =>
      planCatalogApi.create({ code, name: name.trim(), monthlyPriceVnd: Number(price) }),
    onSuccess: onCreated,
  });

  const valid = PLAN_CODE.test(code) && name.trim() !== "" && price !== "";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) create.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Tạo gói mới</DialogTitle>
            <DialogDescription>
              Gói mới <b>chưa mở bán</b> và mới chỉ có các tính năng bắt buộc: chọn tính năng cho
              gói rồi mới mở bán.
            </DialogDescription>
          </DialogHeader>

          <Field
            label="Mã gói"
            hint={
              <p>
                <b>Không sửa được sau khi tạo</b> — mã đi sang Addy Message, Addy Sites và nằm
                trong sổ cái.
              </p>
            }
          >
            <Input
              value={code}
              placeholder="VD: CO_BAN"
              className="font-mono"
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tên hiển thị">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Giá / tháng (đ)" hint={<p>Giá 0đ là gói miễn phí.</p>}>
              <Input
                inputMode="numeric"
                value={price}
                onChange={(e) => setPrice(digitsOnly(e.target.value))}
              />
            </Field>
          </div>

          {create.isError && (
            <p className="text-sm text-destructive">{apiError(create.error).detail}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Huỷ
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? "Đang tạo…" : "Tạo gói"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PlanEditDialog({
  plan,
  onSaved,
  onClose,
}: {
  plan: PlanCatalogRow;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(plan.name);
  const [price, setPrice] = useState(String(plan.monthlyPriceVnd));
  /**
   * Máy chủ có nói cho biểu mẫu này biết trần hiện tại không? (#19052)
   *
   * `null` = nó nói rõ "gói không bao phí cổng". `undefined` = nó KHÔNG nói gì — trường vắng khỏi
   * payload. Hai chuyện đó khác nhau, và chính việc gộp chúng lại đã xoá trần của khách: DTO tầng
   * ứng dụng thiếu trường, ô nhập vì thế luôn rỗng, và ô rỗng được gửi đi như mệnh lệnh "gói không
   * bao". Trần là đầu vào của khoản hoàn phí cổng, nên mất nó là phòng khám mất tiền.
   *
   * Nguyên tắc rút ra, và là lý do giữ lại lớp này sau khi máy chủ đã sửa: **biểu mẫu không được
   * khẳng định điều nó không biết.** Không biết trần thì lần lưu này không nhắc gì tới trần.
   */
  const capKnown = plan.onlinePaymentCapVnd !== undefined;
  // "" = quản trị viên muốn gói KHÔNG bao. Gửi xuống là clearOnlinePaymentCap, không phải -1.
  const [cap, setCap] = useState(
    plan.onlinePaymentCapVnd == null ? "" : String(plan.onlinePaymentCapVnd),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      planCatalogApi.update(plan.id, {
        name: name.trim(),
        monthlyPriceVnd: Number(price),
        ...(capKnown
          ? cap.trim() === ""
            ? { clearOnlinePaymentCap: true }
            : { onlinePaymentCapVnd: Number(cap) }
          : {}),
      }),
    onSuccess: onSaved,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const valid = name.trim() !== "" && price.trim() !== "";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              Sửa gói <span className="font-mono">{plan.code}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tên hiển thị">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Giá / tháng (đ)">
              <Input
                inputMode="numeric"
                value={price}
                onChange={(e) => setPrice(digitsOnly(e.target.value))}
              />
            </Field>
          </div>
          <Field
            label="Doanh thu online gói bao / tháng (đ)"
            hint={
              <>
                {!capKnown && (
                  <p className="text-amber-700 dark:text-amber-500">
                    Máy chủ không trả về trần hiện tại của gói, nên trang này không biết nó đang là
                    bao nhiêu. Lần lưu này sẽ <b>giữ nguyên</b> trần đang có thay vì xoá nó.
                  </p>
                )}
                <p>
                  Chỉ tính tiền thu qua <b>cổng có phí</b> (MoMo, VNPAY, ZaloPay…). Tiền vào bằng
                  mã QR ngân hàng <b>không tính</b> — nó không tốn phí. Vượt trần thì phòng khám
                  trả phần vượt.
                </p>
              </>
            }
          >
            <Input
              inputMode="numeric"
              disabled={!capKnown}
              placeholder="Để trống = gói không bao, phòng khám tự trả phí cổng"
              value={cap}
              onChange={(e) => setCap(digitsOnly(e.target.value))}
            />
          </Field>

          {plan.tenantsOnPlan > 0 && (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              {plan.tenantsOnPlan} phòng khám đang ở gói này. Đổi giá <b>không</b> đổi kỳ họ đã trả
              tiền — chỉ áp cho lần mua tiếp theo, và chỉ với phòng khám không có giá riêng.
            </p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Huỷ
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? "Đang lưu…" : "Lưu"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
